import { useState, useMemo } from 'react'
import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import LinearProgress from '@mui/material/LinearProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'

import { masks } from 'src/utils/masks'
import { fNumber, fPercent } from 'src/utils/format-number'
import Iconify from 'src/components/iconify'
import Scrollbar from 'src/components/scrollbar'
import {
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  TablePaginationCustom,
  emptyRows,
  useTable,
} from 'src/components/table'

const PAYMENT_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  money: { label: 'Dinheiro', icon: 'solar:dollar-minimalistic-bold', color: '#16a34a' },
  credit: { label: 'Crédito', icon: 'solar:card-bold', color: '#2563eb' },
  debit: { label: 'Débito', icon: 'solar:card-2-bold', color: '#0ea5e9' },
  pix: { label: 'Pix', icon: 'simple-icons:pix', color: '#06b6d4' },
  card: { label: 'CashLess', icon: 'solar:wallet-money-bold', color: '#a855f7' },
}

const TABLE_HEAD = [
  { id: 'rank', label: '#', width: 48 },
  { id: 'seller', label: 'Vendedor', width: 220 },
  { id: 'totalSales', label: 'Faturamento', width: 200 },
  { id: 'totalOrders', label: 'Pedidos', width: 100, align: 'right' },
  { id: 'averageTicket', label: 'Ticket Médio', width: 110, align: 'right' },
  { id: 'netSales', label: 'Líquido', width: 110, align: 'right' },
  { id: 'topPayment', label: 'Pagamento Top', width: 130 },
  { id: 'specialization', label: 'Especialização', width: 180 },
  { id: 'actions', label: '', width: 56, align: 'right' },
]

export type SellerRow = {
  sellerId: string
  sellerName: string
  sellerEmail?: string
  totalSales: number
  totalOrders: number
  totalTax: number
  netSales: number
  averageTicket: number
  totalItems?: number
  uniqueProductsCount?: number
  firstSale?: string | Date | null
  lastSale?: string | Date | null
  specialization?: {
    productName: string
    categoryName: string
    totalQuantity: number
    totalRevenue: number
  } | null
  topProducts?: Array<{
    productName: string
    categoryName: string
    totalQuantity: number
    totalRevenue: number
  }>
  paymentTypeStats?: Record<string, { totalAmount: number; count: number }>
}

type Props = {
  data: SellerRow[]
  onOpenDetail: (seller: SellerRow) => void
}

export default function SellerTable({ data, onOpenDetail }: Props) {
  const table = useTable({ defaultRowsPerPage: 10, defaultOrderBy: 'totalSales' })
  const [search, setSearch] = useState('')

  const totalSalesAll = useMemo(
    () => data.reduce((acc, s) => acc + (s.totalSales || 0), 0),
    [data],
  )

  const filteredData = useMemo(() => {
    if (!search) return data
    const term = search.toLowerCase()
    return data.filter(
      (s) =>
        s.sellerName?.toLowerCase().includes(term) ||
        s.sellerEmail?.toLowerCase().includes(term),
    )
  }, [data, search])

  const notFound = !filteredData.length

  return (
    <Card sx={{ borderRadius: 2 }}>
      <CardHeader
        title={
          <Stack direction='row' alignItems='center' spacing={1.25}>
            <Iconify icon='solar:chart-2-bold' width={20} sx={{ color: 'primary.main' }} />
            <Typography variant='subtitle1' fontWeight={700} sx={{ fontSize: 15 }}>
              Performance Detalhada
            </Typography>
            <Chip
              size='small'
              label={`${data.length} ${data.length === 1 ? 'vendedor' : 'vendedores'}`}
              color='primary'
              variant='soft'
              sx={{ height: 22, fontSize: 11, fontWeight: 700 }}
            />
          </Stack>
        }
        action={
          <TextField
            size='small'
            placeholder='Buscar vendedor...'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 240 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <Iconify icon='eva:search-fill' width={18} sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position='end'>
                  <IconButton size='small' onClick={() => setSearch('')}>
                    <Iconify icon='eva:close-fill' width={16} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        }
        sx={{ py: 1.5, px: 2 }}
      />

      <Scrollbar>
        <Table size='small' sx={{ minWidth: 1100 }}>
          <TableHeadCustom
            order={table.order}
            orderBy={table.orderBy}
            headLabel={TABLE_HEAD}
            rowCount={filteredData.length}
            numSelected={table.selected.length}
            onSort={table.onSort}
            onRequestSort={table.onSort}
            sx={{
              '& th': {
                py: 1,
                fontSize: 12,
                fontWeight: 700,
                bgcolor: 'background.neutral',
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: 0.3,
              },
            }}
          />

          <TableBody>
            {filteredData
              .slice(
                table.page * table.rowsPerPage,
                table.page * table.rowsPerPage + table.rowsPerPage,
              )
              .map((row, index) => (
                <SellerTableRow
                  key={row.sellerId}
                  row={row}
                  rank={table.page * table.rowsPerPage + index + 1}
                  totalSalesAll={totalSalesAll}
                  onOpenDetail={onOpenDetail}
                />
              ))}

            <TableEmptyRows
              height={42}
              emptyRows={emptyRows(table.page, table.rowsPerPage, filteredData.length)}
            />

            <TableNoData notFound={notFound} />
          </TableBody>
        </Table>
      </Scrollbar>

      <TablePaginationCustom
        count={filteredData.length}
        page={table.page}
        rowsPerPage={table.rowsPerPage}
        onPageChange={table.onChangePage}
        onRowsPerPageChange={table.onChangeRowsPerPage}
        dense={table.dense}
        onChangeDense={table.onChangeDense}
      />
    </Card>
  )
}

// ----------------------------------------------------------------------

function SellerTableRow({
  row,
  rank,
  totalSalesAll,
  onOpenDetail,
}: {
  row: SellerRow
  rank: number
  totalSalesAll: number
  onOpenDetail: (seller: SellerRow) => void
}) {
  const share = totalSalesAll ? (row.totalSales / totalSalesAll) * 100 : 0

  const topPaymentEntry = useMemo(() => {
    if (!row.paymentTypeStats) return null
    const entries = Object.entries(row.paymentTypeStats)
    if (!entries.length) return null
    return entries.reduce((max, curr) =>
      curr[1].totalAmount > max[1].totalAmount ? curr : max,
    )
  }, [row.paymentTypeStats])

  const topPayment = topPaymentEntry
    ? {
        type: topPaymentEntry[0],
        ...topPaymentEntry[1],
        share: row.totalSales ? (topPaymentEntry[1].totalAmount / row.totalSales) * 100 : 0,
      }
    : null

  const rankMedal = (() => {
    if (rank === 1) return { emoji: '🥇', bg: 'linear-gradient(135deg, #fbbf24, #f59e0b)' }
    if (rank === 2) return { emoji: '🥈', bg: 'linear-gradient(135deg, #cbd5e1, #94a3b8)' }
    if (rank === 3) return { emoji: '🥉', bg: 'linear-gradient(135deg, #f97316, #ea580c)' }
    return null
  })()

  return (
    <TableRow
      hover
      sx={{
        '& td': {
          py: 1,
          fontSize: 13,
          borderBottom: '1px solid',
          borderColor: 'divider',
        },
        ...(rank <= 3 && {
          bgcolor: rank === 1 ? 'rgba(251, 191, 36, 0.04)' : 'transparent',
        }),
      }}
    >
      {/* RANK */}
      <TableCell>
        {rankMedal ? (
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 16,
              background: rankMedal.bg,
              boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
            }}
          >
            {rankMedal.emoji}
          </Box>
        ) : (
          <Typography
            sx={{
              width: 28,
              textAlign: 'center',
              fontWeight: 700,
              fontSize: 12,
              color: 'text.secondary',
            }}
          >
            {rank}º
          </Typography>
        )}
      </TableCell>

      {/* SELLER */}
      <TableCell>
        <Stack spacing={0.25}>
          <Typography variant='body2' fontWeight={600} sx={{ fontSize: 13 }} noWrap>
            {row.sellerName}
          </Typography>
          {row.sellerEmail ? (
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ fontSize: 11 }}
              noWrap
            >
              {row.sellerEmail}
            </Typography>
          ) : null}
        </Stack>
      </TableCell>

      {/* FATURAMENTO + SHARE BAR */}
      <TableCell>
        <Stack spacing={0.5}>
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <Typography variant='body2' fontWeight={700} sx={{ fontSize: 13 }}>
              {masks.currencyAllPlatforms(row.totalSales)}
            </Typography>
            <Typography
              variant='caption'
              fontWeight={700}
              sx={{
                fontSize: 11,
                color: 'primary.main',
                bgcolor: 'primary.lighter',
                px: 0.75,
                py: 0.125,
                borderRadius: 0.5,
              }}
            >
              {fPercent(share)}
            </Typography>
          </Stack>
          <LinearProgress
            variant='determinate'
            value={Math.min(share, 100)}
            sx={{
              height: 5,
              borderRadius: 2,
              bgcolor: 'divider',
              '& .MuiLinearProgress-bar': {
                borderRadius: 2,
                background:
                  rank === 1
                    ? 'linear-gradient(90deg, #fbbf24, #f59e0b)'
                    : 'linear-gradient(90deg, #6366f1, #8b5cf6)',
              },
            }}
          />
        </Stack>
      </TableCell>

      {/* PEDIDOS */}
      <TableCell align='right'>
        <Typography variant='body2' fontWeight={600} sx={{ fontSize: 13 }}>
          {fNumber(row.totalOrders)}
        </Typography>
        {row.totalItems ? (
          <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11 }}>
            {fNumber(row.totalItems)} itens
          </Typography>
        ) : null}
      </TableCell>

      {/* TICKET MÉDIO */}
      <TableCell align='right'>
        <Typography variant='body2' fontWeight={600} sx={{ fontSize: 13 }}>
          {masks.currencyAllPlatforms(row.averageTicket)}
        </Typography>
      </TableCell>

      {/* LÍQUIDO */}
      <TableCell align='right'>
        <Typography variant='body2' fontWeight={600} sx={{ fontSize: 13, color: 'success.main' }}>
          {masks.currencyAllPlatforms(row.netSales)}
        </Typography>
        {row.totalTax ? (
          <Typography variant='caption' color='text.secondary' sx={{ fontSize: 11 }}>
            -{masks.currencyAllPlatforms(row.totalTax)} tx
          </Typography>
        ) : null}
      </TableCell>

      {/* PAGAMENTO TOP */}
      <TableCell>
        {topPayment ? (
          <Tooltip
            title={
              <Box>
                <Typography variant='caption' fontWeight={700}>
                  {(PAYMENT_LABELS[topPayment.type] || {}).label || topPayment.type}
                </Typography>
                <br />
                {masks.currencyAllPlatforms(topPayment.totalAmount)} ({fPercent(topPayment.share)})
                <br />
                {fNumber(topPayment.count)} pedidos
              </Box>
            }
            placement='top'
            arrow
          >
            <Stack direction='row' alignItems='center' spacing={0.75}>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: `${(PAYMENT_LABELS[topPayment.type] || {}).color || '#64748b'}22`,
                  color: (PAYMENT_LABELS[topPayment.type] || {}).color || '#64748b',
                }}
              >
                <Iconify
                  icon={(PAYMENT_LABELS[topPayment.type] || {}).icon || 'solar:wallet-bold'}
                  width={12}
                />
              </Box>
              <Stack spacing={0} sx={{ minWidth: 0 }}>
                <Typography
                  variant='caption'
                  fontWeight={700}
                  sx={{ fontSize: 11, lineHeight: 1.2 }}
                  noWrap
                >
                  {(PAYMENT_LABELS[topPayment.type] || {}).label || topPayment.type}
                </Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ fontSize: 10, lineHeight: 1.2 }}
                >
                  {fPercent(topPayment.share)}
                </Typography>
              </Stack>
            </Stack>
          </Tooltip>
        ) : (
          <Typography variant='caption' color='text.disabled' sx={{ fontSize: 11 }}>
            —
          </Typography>
        )}
      </TableCell>

      {/* ESPECIALIZAÇÃO */}
      <TableCell>
        {row.specialization?.productName ? (
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography
              variant='body2'
              fontWeight={600}
              sx={{ fontSize: 12, lineHeight: 1.3 }}
              noWrap
            >
              {row.specialization.productName}
            </Typography>
            <Stack direction='row' alignItems='center' spacing={0.5}>
              <Chip
                label={row.specialization.categoryName}
                size='small'
                variant='soft'
                color='info'
                sx={{ height: 16, fontSize: 9, fontWeight: 700, '& .MuiChip-label': { px: 0.75 } }}
              />
              <Typography variant='caption' color='text.secondary' sx={{ fontSize: 10 }}>
                {fNumber(row.specialization.totalQuantity)} un.
              </Typography>
            </Stack>
          </Stack>
        ) : (
          <Typography variant='caption' color='text.disabled' sx={{ fontSize: 11 }}>
            —
          </Typography>
        )}
      </TableCell>

      {/* ACTIONS */}
      <TableCell align='right'>
        <Tooltip title='Ver detalhes' arrow placement='top'>
          <IconButton
            size='small'
            onClick={() => onOpenDetail(row)}
            sx={{
              color: 'primary.main',
              bgcolor: 'primary.lighter',
              '&:hover': { bgcolor: 'primary.light', color: 'primary.dark' },
            }}
          >
            <Iconify icon='solar:eye-bold' width={16} />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  )
}
